import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloProvider,
  InMemoryCache,
  ApolloClient,
  ApolloLink,
  from,
  createHttpLink,
  fromPromise,
  split,
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import fetch from 'cross-fetch';
import { onError } from '@apollo/client/link/error';

import { getEnvironment } from './config/environment';

export interface AuthApolloContextProps {
  clientAuthenticated: boolean;
}

export const AuthApolloContext: React.Context<AuthApolloContextProps> = createContext(
  {} as AuthApolloContextProps
);
export const useAuthApollo = (): AuthApolloContextProps => useContext(AuthApolloContext);

interface AuthApolloProviderProps {
  children: ReactNode;
}

function AuthApolloProvider(props: AuthApolloProviderProps): JSX.Element | null {
  const { children } = props;
  const { getAccessTokenSilently, isLoading: auth0Loading, error, user: auth0User } = useAuth0();
  const accessToken = useRef<string | undefined>(undefined);
  const [clientAuthenticated, setClientAuthenticated] = useState(false);
  const [loadingAccessToken, setLoadingAccessToken] = useState(false);
  const { backend } = getEnvironment();
  const { rudderanalytics } = window;

  const clientAuthenticatedProp = useMemo(() => ({ clientAuthenticated }), [clientAuthenticated]);

  useEffect(() => {
    setLoadingAccessToken(true);
    getAccessTokenSilently()
      .then((token) => {
        accessToken.current = token;
        setClientAuthenticated(true);
      })
      .catch((tokenError) => console.warn('Chat could not get access token: ', tokenError.message))
      .finally(() => setLoadingAccessToken(false));
  }, [getAccessTokenSilently]);

  const authMiddleware = new ApolloLink((operation, forward) => {
    const token = accessToken.current;
    // Add the accessToken to the authorization header if we have it
    if (token) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      }));
    }

    return forward(operation);
  });

  // eslint-disable-next-line consistent-return
  const errorLink = onError(({ networkError, operation, forward }) => {
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
      // We need to refresh the token from Auth0, if that's successful then replay the call
      // if not, we don't return anything and the call chain stops
      return (
        fromPromise(
          getAccessTokenSilently({ ignoreCache: true })
            .then((token: string) => {
              accessToken.current = token;
              return token;
            })
            .catch((tokenError: Error) => {
              console.warn('Chat could not get access token: ', tokenError);
              return false;
            })
        )
          // Filters out failed token refresh calls by casting the return value above to a boolean
          .filter((value) => Boolean(value))

          // Retry the request with an additional custom header for testing/logging
          .flatMap(() => {
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: { ...oldHeaders, 'x-apollo-replay': true },
            });

            return forward(operation);
          })
      );
    }
  });

  if (auth0Loading || loadingAccessToken) {
    return null;
  }

  if (error) {
    console.error(error);
  }

  const httpLink = createHttpLink({
    uri: `${backend}/graphql`,
    fetch,
  });

  const wsLink = new WebSocketLink({
    uri: `${backend.replace(/http/, 'ws')}/graphql`,
    options: {
      reconnect: true,
      connectionParams: { authToken: accessToken.current },
    },
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([errorLink, authMiddleware, splitLink]),
  });

  rudderanalytics.identify(auth0User?.sub);

  return (
    <ApolloProvider client={apolloClient}>
      <AuthApolloContext.Provider value={clientAuthenticatedProp}>
        {children}
      </AuthApolloContext.Provider>
    </ApolloProvider>
  );
}

export default AuthApolloProvider;
